import { resolveCoords } from "./airports";
import { stampCancelOnLog } from "./cancelReason";
import { dualClockStamp } from "./clock";
import { displayFlightNumber } from "./flightNumber";
import { hydrateFuelUnit } from "./fuelUnits";
import { LOG_STATUS_FROM_LIFECYCLE, sortOpsBoard, ticketLifecycle } from "./opsBoard";
import { phaseLooksAirborne, type SiFlightSession, type SiPhaseGuess } from "./sayintentions";
import { landingSplit } from "./sunAngle";
import { parseAtdIso, projectLogAtd, ticketAtdIso } from "./ticketAtd";
import { projectTripOps } from "./tripDelayFuel";
import type { ArrivalReport, DeskRole, FlightLogEntry, FlightLogStatus, Mission, MissionStatus, SceneryAirport } from "./types";
import type { TailTripTelemetry } from "./simTelemetry";

export function newFlightLogId(tripId: string, now = new Date()): string {
  return `log-${tripId}-${now.getTime().toString(36)}`;
}

export function logStatusFromMission(status: MissionStatus): FlightLogStatus | null {
  if (status === "pending") return "filed";
  if (status === "in-progress") return "accepted";
  if (status === "completed") return "complete";
  if (status === "cancelled") return "cancelled";
  return null;
}

export function logStatusFromTicket(mission: Pick<Mission, "status" | "lifecycle">): FlightLogStatus {
  return LOG_STATUS_FROM_LIFECYCLE[ticketLifecycle(mission)] ?? logStatusFromMission(mission.status) ?? "filed";
}

export function flightLogFromMission(
  mission: Mission,
  opts: { assignedPilot: string; role: DeskRole; now?: Date },
): FlightLogEntry {
  const now = opts.now ?? new Date();
  const stamp = dualClockStamp(now);
  const fromMission = logStatusFromTicket(mission);
  return {
    id: newFlightLogId(mission.id, now),
    tripId: mission.id,
    callsign: displayFlightNumber(mission) || mission.callsign,
    flightNumber: mission.flightNumber || displayFlightNumber(mission) || undefined,
    registration: mission.registration,
    aircraftName: mission.aircraftName,
    flightType: mission.flightType,
    originIcao: mission.originIcao,
    destIcao: mission.destIcao,
    route: `${mission.originIcao}–${mission.destIcao}`,
    assignedPilot: opts.assignedPilot,
    role: opts.role,
    notes: "",
    status: fromMission,
    createdAtUtc: stamp.utc,
    createdAtLocal: stamp.local,
    updatedAtUtc: stamp.utc,
    updatedAtLocal: stamp.local,
    plannedEteMin: mission.eteMin,
    departureIso: mission.departureIso,
    actualDepartureUtc: ticketAtdIso(mission) ?? undefined,
    departedAtUtc: fromMission === "airborne" || fromMission === "complete" ? stamp.utc : undefined,
    departedAtLocal: fromMission === "airborne" || fromMission === "complete" ? stamp.local : undefined,
    arrivedAtUtc: fromMission === "complete" ? stamp.utc : undefined,
    arrivedAtLocal: fromMission === "complete" ? stamp.local : undefined,
  };
}

export function applyMissionStatusToLog(
  log: FlightLogEntry,
  status: MissionStatus,
  now = new Date(),
): FlightLogEntry {
  const next = logStatusFromMission(status);
  if (!next) return log;
  return applyLogStatus(log, next, now);
}

export function applyLogStatus(log: FlightLogEntry, status: FlightLogStatus, now = new Date()): FlightLogEntry {
  const stamp = dualClockStamp(now);
  const next: FlightLogEntry = {
    ...log,
    status,
    updatedAtUtc: stamp.utc,
    updatedAtLocal: stamp.local,
  };
  if (
    (status === "airborne" ||
      status === "diverting" ||
      status === "arrived" ||
      status === "diverted" ||
      status === "complete") &&
    !log.departedAtUtc
  ) {
    next.departedAtUtc = stamp.utc;
    next.departedAtLocal = stamp.local;
  }
  if ((status === "arrived" || status === "diverted") && !log.arrivedAtUtc) {
    next.arrivedAtUtc = stamp.utc;
    next.arrivedAtLocal = stamp.local;
  }
  if (status === "complete") {
    if (!log.departedAtUtc && !next.departedAtUtc) {
      next.departedAtUtc = stamp.utc;
      next.departedAtLocal = stamp.local;
    }
    // A touchdown already on the log stays. Completing the ticket must not
    // re-stamp arrival with the submit or auto-complete clock.
    if (!log.arrivedAtUtc) {
      next.arrivedAtUtc = stamp.utc;
      next.arrivedAtLocal = stamp.local;
    }
  }
  return next;
}

/** Write off-blocks, wheels-off, touchdown and on-blocks from the closed session. */
export function stampLogFromClosedTrip(
  log: FlightLogEntry,
  trip: Pick<
    TailTripTelemetry,
    "offBlocksUtc" | "airborneUtc" | "landedUtc" | "onBlocksUtc" | "airTimeHours" | "destIcao"
  >,
  scenery: readonly SceneryAirport[] = [],
): FlightLogEntry {
  const off = trip.offBlocksUtc || log.offBlocksUtc;
  const on = trip.onBlocksUtc || log.onBlocksUtc;
  const wheels = trip.airborneUtc || log.wheelsOffUtc;
  const touch = trip.landedUtc || log.touchdownUtc;
  const airTimeMin =
    trip.airTimeHours > 0 ? Math.round(trip.airTimeHours * 600) / 10 : log.airTimeMin;
  const dest = trip.destIcao || log.destIcao;
  const coords = resolveCoords(dest, [...scenery]);
  const landings = landingSplit(touch, coords?.lat, coords?.lon);
  const arrived = log.arrival ? log.arrivedAtUtc : touch || log.arrivedAtUtc;
  const arrivedLocal = arrived ? dualClockStamp(new Date(arrived)).local : log.arrivedAtLocal;
  return {
    ...log,
    offBlocksUtc: off,
    onBlocksUtc: on,
    wheelsOffUtc: wheels,
    touchdownUtc: touch,
    airTimeMin,
    landingsDay: touch ? landings.landingsDay : log.landingsDay,
    landingsNight: touch ? landings.landingsNight : log.landingsNight,
    departedAtUtc: off || log.departedAtUtc,
    departedAtLocal: off ? dualClockStamp(new Date(off)).local : log.departedAtLocal,
    arrivedAtUtc: arrived,
    arrivedAtLocal: arrivedLocal,
  };
}

export function applySiSessionToLog(
  log: FlightLogEntry,
  session: SiFlightSession,
  phase: SiPhaseGuess,
  now = new Date(),
): FlightLogEntry {
  const stamp = dualClockStamp(now);
  let next: FlightLogEntry = {
    ...log,
    siFlightId: session.flightId || log.siFlightId,
    updatedAtUtc: stamp.utc,
    updatedAtLocal: stamp.local,
  };
  if (log.status === "filed" && phaseLooksAirborne(phase)) {
    next = applyLogStatus(next, "airborne", now);
  }
  return next;
}

/** Block minutes: on-blocks − off-blocks, else the departed/arrived pair. */
export function blockTimeMin(log: FlightLogEntry): number | null {
  const start = log.offBlocksUtc || log.departedAtUtc;
  const end = log.onBlocksUtc || log.arrivedAtUtc;
  if (!start || !end) return null;
  const a = new Date(start).getTime();
  const b = new Date(end).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return null;
  return Math.round((b - a) / 60_000);
}

export function formatBlockHours(min: number | null): string {
  if (min == null || min < 0) return "—";
  return (min / 60).toFixed(1);
}

export function logbookHoursLabel(log: FlightLogEntry): string {
  const block = blockTimeMin(log);
  if (block != null) return `${formatBlockHours(block)} blk`;
  if (log.plannedEteMin && log.plannedEteMin > 0) {
    return `${formatBlockHours(log.plannedEteMin)} planned`;
  }
  return "—";
}

export function logsForRole(
  logs: FlightLogEntry[],
  role: DeskRole,
  pilotName: string,
  opts?: { companyHistory?: boolean; crewId?: string | null },
): FlightLogEntry[] {
  if (role === "dispatcher" || opts?.companyHistory) return logs;
  const name = pilotName.trim().toLowerCase();
  const crewId = (opts?.crewId ?? "").trim().toLowerCase();
  if (!name && !crewId) return logs;
  return logs.filter((l) => {
    const assigned = l.assignedPilot.trim().toLowerCase();
    if (!assigned) return true;
    if (name && assigned === name) return true;
    if (crewId && (assigned === crewId || assigned.includes(crewId))) return true;
    return false;
  });
}

export type FlightLogTabId = "trips" | "logbook" | "postflight" | "aar";

/** Deep-link from Dashboard / Map into Flight Log. Not persisted. */
export type FlightLogFocus = {
  logId?: string;
  tab: FlightLogTabId;
};

export function logRecencyMs(log: FlightLogEntry): number {
  const stamps = [log.updatedAtUtc, log.arrivedAtUtc, log.arrival?.reportedAtUtc, log.createdAtUtc];
  let max = 0;
  for (const stamp of stamps) {
    if (!stamp) continue;
    const t = new Date(stamp).getTime();
    if (Number.isFinite(t) && t > max) max = t;
  }
  return max;
}

export function sortLogsRecent(logs: FlightLogEntry[]): FlightLogEntry[] {
  return [...logs].sort((a, b) => logRecencyMs(b) - logRecencyMs(a) || b.id.localeCompare(a.id));
}

export function lastArrivalLog(logs: FlightLogEntry[]): FlightLogEntry | undefined {
  return sortLogsRecent(logs.filter((l) => Boolean(l.arrival)))[0];
}

export function lastCompletedLog(logs: FlightLogEntry[]): FlightLogEntry | undefined {
  return sortLogsRecent(logs.filter((l) => l.status === "complete"))[0];
}

/**
 * Log Emerson should open after landing / arrival. Uses on-file rows only.
 */
export function lastFindableLog(logs: FlightLogEntry[]): FlightLogEntry | undefined {
  return (
    lastArrivalLog(logs) ??
    lastCompletedLog(logs) ??
    sortLogsRecent(logs.filter((l) => l.status === "airborne" || l.status === "filed"))[0]
  );
}

export function logFocusFor(log: FlightLogEntry): FlightLogFocus {
  if (log.status === "complete") return { logId: log.id, tab: "aar" };
  return { logId: log.id, tab: "postflight" };
}

export function logCueLabel(log: FlightLogEntry): string {
  const when = log.arrival
    ? "Last arrival"
    : log.status === "complete"
      ? "Last complete"
      : log.status === "airborne"
        ? "Open trip"
        : log.status === "cancelled"
          ? "Cancelled trip"
          : "Filed trip";
  return `${when}  ${displayFlightNumber(log) || log.callsign}  ${log.route}`;
}

export function logListStamp(log: FlightLogEntry): string {
  const raw = log.arrival?.reportedAtUtc || log.arrivedAtUtc || log.updatedAtUtc || log.createdAtUtc;
  if (!raw) return "";
  return raw.slice(0, 16).replace("T", " ");
}

export function findLogForTrip(logs: FlightLogEntry[], tripId: string): FlightLogEntry | undefined {
  return logs.find((l) => l.tripId === tripId);
}

export function upsertLogList(logs: FlightLogEntry[], entry: FlightLogEntry): FlightLogEntry[] {
  const idx = logs.findIndex((l) => l.id === entry.id || l.tripId === entry.tripId);
  if (idx === -1) return [entry, ...logs];
  return logs.map((l, i) => (i === idx ? { ...l, ...entry, id: l.id } : l));
}

export function hydrateArrivalReport(raw?: unknown): ArrivalReport | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const row = raw as Record<string, unknown>;
  const notes = typeof row.notes === "string" ? row.notes : "";
  const maintenanceNotes = typeof row.maintenanceNotes === "string" ? row.maintenanceNotes : "";
  const reportedAtUtc = typeof row.reportedAtUtc === "string" ? row.reportedAtUtc : "";
  const reportedAtLocal = typeof row.reportedAtLocal === "string" ? row.reportedAtLocal : "";
  const engineHours =
    typeof row.engineHours === "number" && Number.isFinite(row.engineHours) && row.engineHours > 0
      ? Math.round(row.engineHours * 100) / 100
      : undefined;
  if (!reportedAtUtc && !notes && !maintenanceNotes && row.fuelUsedAmount == null && !row.nothingToReport && engineHours == null) {
    return undefined;
  }
  const amount = typeof row.fuelUsedAmount === "number" && Number.isFinite(row.fuelUsedAmount) ? row.fuelUsedAmount : undefined;
  return {
    reportedAtUtc: reportedAtUtc || new Date().toISOString(),
    reportedAtLocal,
    notes,
    maintenanceNotes,
    nothingToReport: Boolean(row.nothingToReport),
    fuelUsedAmount: amount,
    fuelUsedUnit: row.fuelUsedUnit ? hydrateFuelUnit(row.fuelUsedUnit) : undefined,
    fuelUsedGal:
      typeof row.fuelUsedGal === "number" && Number.isFinite(row.fuelUsedGal) ? row.fuelUsedGal : undefined,
    fuelCostCad:
      typeof row.fuelCostCad === "number" && Number.isFinite(row.fuelCostCad) ? row.fuelCostCad : undefined,
    engineHours,
    squawkIds: Array.isArray(row.squawkIds)
      ? row.squawkIds.filter((id): id is string => typeof id === "string" && Boolean(id))
      : undefined,
  };
}

const LOG_STATUSES: readonly FlightLogStatus[] = [
  "filed",
  "accepted",
  "taxi",
  "airborne",
  "diverting",
  "arrived",
  "diverted",
  "complete",
  "cancelled",
];

function hydrateLogStatus(raw: FlightLogEntry): FlightLogStatus {
  if (raw.cancelReason && raw.status !== "complete") return "cancelled";
  if ((LOG_STATUSES as readonly string[]).includes(raw.status)) return raw.status;
  return "filed";
}

export function hydrateFlightLog(raw: FlightLogEntry): FlightLogEntry {
  const arrival = hydrateArrivalReport(raw.arrival);
  return {
    ...raw,
    status: hydrateLogStatus(raw),
    flightNumber: raw.flightNumber || undefined,
    actualDepartureUtc: parseAtdIso(raw.actualDepartureUtc) || undefined,
    arrival,
  };
}

export function syncLogIdentityFromMissions(
  logs: FlightLogEntry[],
  missions: ReadonlyArray<Mission>,
): FlightLogEntry[] {
  return projectLogsFromMissions(logs, missions);
}

/**
 * Flight Log is a projection of canonical tickets — same tripId, callsign,
 * live STD, route, lifecycle/status, cancel. History fields (wheels, AAR)
 * stay on the log row.
 */
export function projectLogsFromMissions(
  logs: FlightLogEntry[],
  missions: ReadonlyArray<Mission>,
  opts?: { assignedPilot?: string; role?: DeskRole; now?: Date; omitTripIds?: readonly string[] },
): FlightLogEntry[] {
  const now = opts?.now ?? new Date();
  const omitTrips = new Set((opts?.omitTripIds ?? []).map((id) => id.trim()).filter(Boolean));
  const byTrip = new Map(logs.map((l) => [l.tripId, l]));
  const projected: FlightLogEntry[] = [];
  for (const mission of missions) {
    if (omitTrips.has(mission.id)) continue;
    const existing = byTrip.get(mission.id);
    const nextStatus = logStatusFromTicket(mission);
    const base =
      existing ??
      flightLogFromMission(mission, {
        assignedPilot: opts?.assignedPilot ?? "",
        role: opts?.role ?? "dispatcher",
        now,
      });
    const flightNumber = displayFlightNumber(mission) || base.flightNumber || base.callsign;
    const cancelled = mission.status === "cancelled" || nextStatus === "cancelled";
    let next: FlightLogEntry = {
      ...base,
      tripId: mission.id,
      callsign: flightNumber,
      flightNumber,
      registration: mission.registration || base.registration,
      aircraftName: mission.aircraftName || base.aircraftName,
      flightType: mission.flightType || base.flightType,
      originIcao: mission.originIcao,
      destIcao: mission.destIcao,
      divertToIcao: mission.divertToIcao,
      divertReason: mission.divertReason,
      route: `${mission.originIcao}–${mission.destIcao}`,
      plannedEteMin: mission.eteMin,
      departureIso: mission.departureIso,
      status: nextStatus,
      cancelReason: cancelled ? mission.cancelReason ?? base.cancelReason : base.cancelReason,
      cancelReasonNote: cancelled ? mission.cancelReasonNote ?? base.cancelReasonNote : base.cancelReasonNote,
    };
    if (next.status !== base.status) {
      next = applyLogStatus(next, next.status, now);
    }
    next = projectLogAtd(next, ticketAtdIso(mission));
    if (cancelled && mission.cancelReason) {
      next = stampCancelOnLog(next, {
        cancelReason: mission.cancelReason,
        cancelReasonNote: mission.cancelReasonNote,
        now,
      });
    }
    next = { ...next, ...projectTripOps(mission) };
    projected.push(next);
    byTrip.delete(mission.id);
  }
  // The log is a projection of the board, not a second source of truth. A row
  // whose ticket is gone and that never left the gate is a ghost — keep only
  // orphans that carry real history (flown, cancelled with a reason, or timed).
  for (const leftover of byTrip.values()) {
    if (omitTrips.has(leftover.tripId)) continue;
    if (isGhostLogRow(leftover)) continue;
    projected.push(leftover);
  }
  return projected;
}

/** Never-flown leftover with no history behind it. */
export function isGhostLogRow(log: FlightLogEntry): boolean {
  if (log.status !== "filed" && log.status !== "accepted") return false;
  if (log.cancelReason || log.cancelReasonNote) return false;
  if (log.departedAtUtc || log.arrivedAtUtc || log.arrival) return false;
  if (log.siFlightId?.trim() || log.lastPushedAtUtc) return false;
  return !log.notes?.trim();
}

/** Same next-up order as Flight plans / Trips — not recency. */
export function sortLogsForBoard(logs: FlightLogEntry[], missions: readonly Mission[]): FlightLogEntry[] {
  const order = new Map(sortOpsBoard(missions).map((m, i) => [m.id, i]));
  return [...logs].sort((a, b) => {
    const ia = order.has(a.tripId) ? order.get(a.tripId)! : Number.MAX_SAFE_INTEGER;
    const ib = order.has(b.tripId) ? order.get(b.tripId)! : Number.MAX_SAFE_INTEGER;
    if (ia !== ib) return ia - ib;
    return a.id.localeCompare(b.id);
  });
}

export function hydrateFlightLogs(raw?: unknown, missions?: ReadonlyArray<Mission>): FlightLogEntry[] {
  if (!Array.isArray(raw)) return [];
  const logs = raw.filter((row): row is FlightLogEntry => Boolean(row && typeof row === "object")).map(hydrateFlightLog);
  return missions?.length ? syncLogIdentityFromMissions(logs, missions) : logs;
}
